import HeaderLayout from '../common/HeaderLayout';
import React, { useEffect, useState } from "react";
import axios from "axios";

export default function Mudatmarked() {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [districtMudat, setDistrictMudat] = useState({});
  const [savingDistrict, setSavingDistrict] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [savedStatus, setSavedStatus] = useState({}); // Track saved state per district
  const [pendingChanges, setPendingChanges] = useState({}); // Track unsaved changes

  const fetchSummary = async (date) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://api.rangpursadhima.cloud/api/attendance/summary?date=${date}`
      );
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary", err);
    }
    setLoading(false);
  };

  const fetchDistrictMudat = async () => {
    try {
      const res = await axios.get("https://api.rangpursadhima.cloud/api/mudat/districts");
      const map = {};
      res.data.forEach(dm => {
        // TRIM and normalize to UPPERCASE to match with breakdown data
        const district = (dm.district || '').trim().toUpperCase();
        map[district] = {
          mudat_days: dm.mudat_days != null ? dm.mudat_days : null,
          next_mudat_date: dm.next_mudat_date ? dm.next_mudat_date.split('T')[0] : ''
        };
      });
      setDistrictMudat(map);
    } catch (err) {
      console.error("Failed to fetch district mudat", err);
    }
  };

  useEffect(() => {
    fetchSummary(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    fetchDistrictMudat();
  }, []);

  const handleDateChange = (e) => {
    const date = e.target.value;
    if (date) setSelectedDate(date);
  };

  const handleDaysChange = (district, value) => {
    const parsedValue = value === '' ? null : parseInt(value, 10);
    // TRIM and normalize to UPPERCASE for consistency
    const normalizedDistrict = district.trim().toUpperCase();
    
    setDistrictMudat(prev => ({
      ...prev,
      [normalizedDistrict]: {
        ...prev[normalizedDistrict],
        mudat_days: parsedValue
      }
    }));

    // Mark as pending change (unsaved)
    setPendingChanges(prev => ({
      ...prev,
      [normalizedDistrict]: true
    }));

    // Clear saved status when editing
    setSavedStatus(prev => ({
      ...prev,
      [normalizedDistrict]: false
    }));
  };

  const calculateSunday = (days) => {
    if (days == null || days === '' || isNaN(days)) {
      return null;
    }
    
    const daysNum = parseInt(days, 10);
    if (daysNum <= 0) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + daysNum);
    
    let lastSunday = null;
    let dayNumber = 0;
    let current = new Date(today);
    
    while (current <= endDate) {
      if (current.getDay() === 0) {
        lastSunday = new Date(current);
        dayNumber = Math.floor((current - today) / (1000 * 60 * 60 * 24));
      }
      current.setDate(current.getDate() + 1);
    }
    
    if (lastSunday) {
      return {
        date: lastSunday,
        dayNumber: dayNumber,
        formatted: lastSunday.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      };
    }
    return null;
  };

  // Auto-save on blur
  const saveDistrictMudat = async (district) => {
    // TRIM and normalize to UPPERCASE before saving
    const normalizedDistrict = district.trim().toUpperCase();
    const data = districtMudat[normalizedDistrict];
    
    // Skip if no days entered
    if (data?.mudat_days == null || data.mudat_days === '') {
      return false;
    }

    // Skip if no pending changes
    if (!pendingChanges[normalizedDistrict]) {
      return true;
    }
    
    setSavingDistrict(normalizedDistrict);
    try {
      const response = await axios.put("https://api.rangpursadhima.cloud/api/mudat/district", {
        district: normalizedDistrict,
        mudat_days: parseInt(data.mudat_days, 10)
      });
      
      // Update local state with calculated date
      setDistrictMudat(prev => ({
        ...prev,
        [normalizedDistrict]: {
          ...prev[normalizedDistrict],
          next_mudat_date: response.data.next_mudat_date
        }
      }));

      // Mark as saved
      setSavedStatus(prev => ({
        ...prev,
        [normalizedDistrict]: true
      }));

      // Clear pending change
      setPendingChanges(prev => {
        const newPending = { ...prev };
        delete newPending[normalizedDistrict];
        return newPending;
      });
      
      setSavingDistrict(null);
      return true;
    } catch (err) {
      console.error("Failed to save district mudat", err);
      setSavingDistrict(null);
      return false;
    }
  };

  // Save All function
  const saveAllDistricts = async () => {
    const districtsToSave = Object.keys(pendingChanges).filter(district => {
      const normalizedDistrict = district.trim().toUpperCase();
      const data = districtMudat[normalizedDistrict];
      return data?.mudat_days != null && data.mudat_days !== '';
    });

    if (districtsToSave.length === 0) {
      alert('કોઈ ફેરફાર નથી સેવ કરવા માટે');
      return;
    }

    setSavingAll(true);
    let savedCount = 0;
    let failedCount = 0;

    for (const district of districtsToSave) {
      const success = await saveDistrictMudat(district.trim().toUpperCase());
      if (success) {
        savedCount++;
      } else {
        failedCount++;
      }
    }

    setSavingAll(false);
    
    if (failedCount === 0) {
      alert(`✅ બધા ${savedCount} જિલ્લા સેવ થયા!`);
    } else {
      alert(`${savedCount} સેવ થયા, ${failedCount} નિષ્ફળ`);
    }
  };

  const calculateTotalAttendance = () => {
    const userIds = new Set();
    summary?.breakdown?.forEach(row => {
      if (row.morning === "Y" || row.evening === "Y") {
        userIds.add(row.user_id);
      }
    });
    return userIds.size;
  };

  const calculateBreakdownByDistrict = () => {
    const map = {};
    let grandTotal = 0;

    summary?.breakdown?.forEach(row => {
      // TRIM and normalize to UPPERCASE to prevent duplicates from spaces and case differences
      const district = (row.DISTRICT || 'Unknown').trim().toUpperCase();
      const state = (row.STATE || '').trim().toUpperCase();
      
      if (!map[district]) {
        map[district] = {
          DISTRICT: district,
          STATE: state,
          cities: new Set(),
          morning: 0,
          evening: 0,
          users: new Set(),
        };
      }
      
      if (row.CITY_AREA_VILLAGE) {
        map[district].cities.add((row.CITY_AREA_VILLAGE || '').trim().toUpperCase());
      }
      
      if (row.morning === "Y") {
        map[district].morning += 1;
        map[district].users.add(row.user_id);
      }
      if (row.evening === "Y") {
        map[district].evening += 1;
        map[district].users.add(row.user_id);
      }
    });

    const breakdown = Object.values(map).map(loc => {
      const total = loc.users.size;
      grandTotal += total;
      return { 
        ...loc, 
        total,
        cityCount: loc.cities.size
      };
    });

    breakdown.sort((a, b) => b.total - a.total);

    return { breakdown, grandTotal };
  };

  const calculateBreakdownByLocation = () => {
    const map = {};
    let grandTotal = 0;

    summary?.breakdown?.forEach(row => {
      // TRIM and normalize to UPPERCASE to prevent duplicates
      const city = (row.CITY_AREA_VILLAGE || '').trim().toUpperCase();
      const district = (row.DISTRICT || '').trim().toUpperCase();
      const state = (row.STATE || '').trim().toUpperCase();
      
      const key = `${city}||${district}||${state}`;
      if (!map[key]) {
        map[key] = {
          CITY_AREA_VILLAGE: city,
          DISTRICT: district,
          STATE: state,
          morning: 0,
          evening: 0,
          users: new Set(),
        };
      }
      if (row.morning === "Y") {
        map[key].morning += 1;
        map[key].users.add(row.user_id);
      }
      if (row.evening === "Y") {
        map[key].evening += 1;
        map[key].users.add(row.user_id);
      }
    });

    const breakdown = Object.values(map).map(loc => {
      const total = loc.users.size;
      grandTotal += total;
      return { ...loc, total };
    });

    return { breakdown, grandTotal };
  };

  const { breakdown: districtBreakdown, grandTotal } = summary ? calculateBreakdownByDistrict() : { breakdown: [], grandTotal: 0 };

  const pendingCount = Object.keys(pendingChanges).length;

  return (
    <HeaderLayout title="">
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">મુદત સમરી</h1>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="border px-4 py-2 rounded-md shadow-sm"
          />
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : summary ? (
          <div>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-100 text-blue-900 p-4 rounded-lg shadow">
                <h2 className="font-semibold text-lg">સવારની મુદત</h2>
                <p className="text-3xl font-bold">{Number(summary.morningCount) || 0}</p>
              </div>
              <div className="bg-green-100 text-green-900 p-4 rounded-lg shadow">
                <h2 className="font-semibold text-lg">સાંજની મુદત</h2>
                <p className="text-3xl font-bold">{Number(summary.eveningCount) || 0}</p>
              </div>
              <div className="bg-purple-100 text-purple-900 p-4 rounded-lg shadow">
                <h2 className="font-semibold text-lg">બન્ને મુદતમાં હાજર</h2>
                <p className="text-3xl font-bold">{Number(summary.bothCount) || 0}</p>
              </div>
              <div className="bg-yellow-100 text-yellow-900 p-4 rounded-lg shadow">
                <h2 className="font-semibold text-lg">કુલ મુદત હાજરી</h2>
                <p className="text-3xl font-bold">{calculateTotalAttendance()}</p>
              </div>
            </div>

            {/* District-wise Mudat Days Settings */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-xl mb-2">જિલ્લા પ્રમાણે મુદત સેટિંગ</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    દિવસો દાખલ કરો અને ઇનપુટ છોડો - ઓટો સેવ થશે
                  </p>
                  <p className="text-xs text-green-600 font-medium">
                    ✅ ઇનપુટ ફિલ્ડ છોડતાં જ ઓટો-સેવ થશે
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {pendingCount > 0 && (
                    <span className="text-sm text-orange-600 font-medium">
                      {pendingCount} ફેરફાર સેવ થયા નથી
                    </span>
                  )}
                  <button
                    onClick={saveAllDistricts}
                    disabled={savingAll || pendingCount === 0}
                    className={`px-4 py-2 rounded font-medium text-white ${
                      pendingCount > 0 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-gray-400 cursor-not-allowed'
                    } disabled:opacity-50`}
                  >
                    {savingAll ? 'સેવ થઈ રહ્યું છે...' : `બધા સેવ કરો (${pendingCount})`}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto rounded-lg shadow mb-10">
              <table className="w-full table-fixed border border-red-600 text-sm">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="p-3 border w-32">જિલ્લા</th>
                    <th className="p-3 border w-28">રાજ્ય</th>
                    <th className="p-3 border w-16">શહેરો</th>
                    <th className="p-3 border w-16">સવાર</th>
                    <th className="p-3 border w-16">સાંજ</th>
                    <th className="p-3 border w-16">કુલ</th>
                    <th className="p-3 border bg-orange-600 w-28">દિવસો</th>
                    <th className="p-3 border bg-orange-600 w-32">આગામી રવિવાર</th>
                    <th className="p-3 border bg-orange-600 w-28">સ્ટેટસ</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {districtBreakdown.map((loc, index) => {
                    const dm = districtMudat[loc.DISTRICT] || { mudat_days: null };
                    const hasDaysSet = dm.mudat_days != null && dm.mudat_days !== '';
                    const calculatedSunday = hasDaysSet ? calculateSunday(dm.mudat_days) : null;
                    const isSaving = savingDistrict === loc.DISTRICT;
                    const isSaved = savedStatus[loc.DISTRICT];
                    const hasPending = pendingChanges[loc.DISTRICT];
                    
                    return (
                      <tr key={index} className={`h-16 ${!hasDaysSet ? 'bg-yellow-50' : ''}`}>
                        <td className="p-3 border text-gray-800 font-medium truncate">
                          {loc.DISTRICT}
                          {!hasDaysSet && (
                            <span className="ml-1 text-xs text-orange-600 font-normal">(નવું)</span>
                          )}
                        </td>
                        <td className="p-3 border text-gray-800 truncate">{loc.STATE}</td>
                        <td className="p-3 border text-center text-gray-600">{loc.cityCount}</td>
                        <td className="p-3 border text-center text-blue-700 font-medium">{loc.morning}</td>
                        <td className="p-3 border text-center text-green-700 font-medium">{loc.evening}</td>
                        <td className="p-3 border text-center font-semibold">{loc.total}</td>
                        <td className="p-2 border bg-orange-50 align-middle">
                          <div className="flex items-center justify-center">
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={dm.mudat_days ?? ''}
                              onChange={(e) => handleDaysChange(loc.DISTRICT, e.target.value)}
                              onBlur={() => saveDistrictMudat(loc.DISTRICT)}
                              className="w-16 h-8 px-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-orange-400"
                              placeholder="—"
                            />
                          </div>
                        </td>
                        <td className="p-2 border bg-orange-50 text-center align-middle h-16">
                          <div className="h-10 flex flex-col items-center justify-center">
                            {calculatedSunday ? (
                              <>
                                <span className="font-medium text-orange-700 text-sm leading-tight">
                                  {calculatedSunday.formatted}
                                </span>
                                <span className="text-xs text-gray-500 leading-tight">
                                  (દિવસ {calculatedSunday.dayNumber})
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="p-2 border text-center bg-orange-50 align-middle h-16">
                          <div className="h-10 flex items-center justify-center">
                            {isSaving ? (
                              <span className="text-xs text-blue-600 whitespace-nowrap">સેવ...</span>
                            ) : isSaved ? (
                              <span className="text-xs text-green-600 font-medium whitespace-nowrap">✅ સેવ થયું</span>
                            ) : hasPending ? (
                              <span className="text-xs text-orange-600 whitespace-nowrap">⏳ pending</span>
                            ) : hasDaysSet ? (
                              <span className="text-xs text-gray-500 whitespace-nowrap">✓ સેવ</span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-red-600 font-semibold text-white">
                    <td colSpan="5" className="p-3 text-right">ગ્રાન્ડ ટોટલ:</td>
                    <td className="p-3 text-center">{grandTotal}</td>
                    <td colSpan="3" className="p-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* City-wise breakdown */}
            <h3 className="font-semibold text-xl mb-2">શહેર/જિલ્લા/રાજ્ય પ્રમાણે લિસ્ટ</h3>
            <div className="overflow-x-auto rounded-lg shadow mb-10">
              <table className="w-full table-auto border border-red-600 text-sm">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="p-3 border">શહેર</th>
                    <th className="p-3 border">જિલ્લા</th>
                    <th className="p-3 border">રાજ્ય</th>
                    <th className="p-3 border">સવારની મુદત</th>
                    <th className="p-3 border">સાંજની મુદત</th>
                    <th className="p-3 border">કુલ બન્ને (Unique)</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {calculateBreakdownByLocation().breakdown.map((loc, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-3 border text-gray-800">{loc.CITY_AREA_VILLAGE}</td>
                      <td className="p-3 border text-gray-800">{loc.DISTRICT}</td>
                      <td className="p-3 border text-gray-800">{loc.STATE}</td>
                      <td className="p-3 border text-center text-blue-700 font-medium">{loc.morning}</td>
                      <td className="p-3 border text-center text-green-700 font-medium">{loc.evening}</td>
                      <td className="p-3 border text-center font-semibold">{loc.total}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-600 font-semibold text-white">
                    <td colSpan="5" className="p-3 text-right">ગ્રાન્ડ ટોટલ (Unique):</td>
                    <td className="p-3 text-center">{calculateBreakdownByLocation().grandTotal}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* User List */}
            <h3 className="font-semibold text-xl mb-2">મુદતમાં હાજરી ભરાવેલ સેવકનું લિસ્ટ</h3>
            <div className="overflow-x-auto rounded-lg shadow">
              <table className="w-full table-auto border border-red-600 text-sm">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="p-3 border">સેવકોડ</th>
                    <th className="p-3 border">નામ</th>
                    <th className="p-3 border">અટક</th>
                    <th className="p-3 border">શહેર</th>
                    <th className="p-3 border">જિલ્લા</th>
                    <th className="p-3 border">સવાર</th>
                    <th className="p-3 border">સાંજ</th>
                    <th className="p-3 border">બન્ને</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {summary?.breakdown?.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-3 border text-gray-800">{user.Sevakcode}</td>
                      <td className="p-3 border text-gray-800">{user.FIRST_NAME}</td>
                      <td className="p-3 border text-gray-800">{user.LAST_NAME}</td>
                      <td className="p-3 border text-gray-800">{user.CITY_AREA_VILLAGE}</td>
                      <td className="p-3 border text-gray-800">{user.DISTRICT}</td>
                      <td className="p-3 border text-center text-blue-700 font-medium">{user.morning}</td>
                      <td className="p-3 border text-center text-green-700 font-medium">{user.evening}</td>
                      <td className="p-3 border text-center font-semibold">{user.both}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">No data available for selected date.</p>
        )}
      </div>
    </HeaderLayout>
  );
}